import styled from '@emotion/styled';
import Icon from '@mdi/react';
import { BaseButton, Variables } from '../../base';
import { ButtonBar } from '../../button-bar';

export const DialogBody = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 22px 16px;
    flex-grow: 1;
    width: 100%;
    box-sizing: border-box;
`;

export const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 18px 0 16px;
    border-bottom: 1px solid ${Variables.Colors.n15};
    margin-bottom: 4px;
`;

export const HeaderIcon = styled(Icon)`
    color: ${Variables.Colors.b300};
    flex: none;
`;

export const HeaderTitle = styled.span`
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    color: ${Variables.Colors.n100};
`;

export const FormSection = styled.div`
    margin-top: 16px;
`;

export const Label = styled.div`
    font-size: 13px;
    font-weight: 500;
    line-height: 1.22;
    color: ${Variables.Colors.n70};
    margin-bottom: 6px;
`;

/** Composite money field: prefix + BaseInput with chrome stripped. */
export const AmountField = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 42px;
    padding: 0 12px;
    border: 1px solid ${Variables.Colors.n15};
    border-radius: 8px;
    background: ${Variables.Swatches.White};

    & > div {
        flex: 1;
        width: 100%;
        min-width: 0;
    }

    input {
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        height: auto !important;
        min-height: 0 !important;
        padding: 0 !important;
        background: transparent !important;
        border-radius: 0 !important;
        font-size: 16px !important;
        font-weight: 600 !important;
        line-height: 1;
        color: ${Variables.Colors.n100};

        &:disabled {
            color: ${Variables.Colors.n50};
            background: transparent !important;
        }
    }
`;

export const AmountPrefix = styled.span`
    color: ${Variables.Colors.n50};
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    flex: none;
`;

export const ErrorText = styled.div`
    margin-top: 6px;
    font-size: 12px;
    color: ${Variables.Swatches.Red6};
`;

export const CollapseToggle = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    height: auto;
    min-width: auto;
    padding: 0;
    border: none;
    background: transparent;
    font-size: 12px;
    font-weight: 500;
    color: ${Variables.Colors.n70};

    &:hover:not(:disabled) {
        color: ${Variables.Colors.b300};
        background: transparent;
    }
`;

export const CollapseIcon = styled(Icon)<{ open?: boolean }>`
    transform: rotate(${props => (props.open ? 90 : 0)}deg);
    transition: transform 0.15s ease;
    color: currentColor;
`;

export const CollapsePanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 10px;
    padding: 12px;
    border: 1px solid ${Variables.Colors.n15};
    border-radius: 8px;
    background: ${Variables.Colors.n5};
`;

export const ApplyModeRow = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
`;

export const ApplyModeButton = styled(BaseButton)<{ $active?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 1;
    height: 38px;
    min-width: auto;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    background: ${props =>
        props.$active ? Variables.ThemeLight.PrimaryOpacity3 : Variables.Swatches.White};
    color: ${props => (props.$active ? Variables.Colors.b300 : Variables.Colors.n70)};
    border: 1px solid
        ${props =>
            props.$active ? Variables.ThemeLight.PrimaryInverted7 : Variables.Colors.n15};

    &:hover:not(:disabled) {
        border-color: ${Variables.ThemeLight.PrimaryInverted4};
        background: ${props =>
            props.$active ? Variables.ThemeLight.PrimaryOpacity3 : Variables.Swatches.White};
        color: ${props => (props.$active ? Variables.Colors.b300 : Variables.Colors.n70)};
    }
`;

export const FifoNote = styled.div`
    display: flex;
    gap: 8px;
    padding: 9px 11px;
    background: ${Variables.ThemeLight.PrimaryOpacity3};
    border: 1px solid ${Variables.ThemeLight.PrimaryInverted7};
    border-radius: 8px;
    font-size: 12px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMedium};
    margin-top: 10px;

    & > svg {
        flex: none;
        margin-top: 1px;
    }
`;

export const ClaimList = styled.div`
    border: 1px solid ${Variables.Colors.n15};
    border-radius: 8px;
    overflow: hidden;
    max-height: 208px;
    overflow-y: auto;
`;

export const ClaimRow = styled(BaseButton)`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 11px;
    width: 100%;
    height: auto;
    min-width: auto;
    padding: 10px 12px;
    border: none;
    border-radius: 0;
    border-bottom: 1px solid ${Variables.Colors.n15};
    background: ${Variables.Swatches.White};
    text-align: left;

    &:last-child {
        border-bottom: none;
    }

    &:hover:not(:disabled) {
        background: ${Variables.Colors.n5};
        color: inherit;
    }

    &:disabled {
        cursor: default;
        opacity: 0.7;
    }
`;

export const CheckBox = styled.span<{ checked?: boolean }>`
    width: 18px;
    height: 18px;
    border-radius: 4px;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => (props.checked ? Variables.Colors.b300 : Variables.Swatches.White)};
    border: 1px solid ${props => (props.checked ? Variables.Colors.b300 : Variables.Colors.n15)};
    color: ${Variables.Swatches.White};
`;

export const ClaimMeta = styled.div`
    flex: 1;
    min-width: 0;
`;

export const ClaimTitle = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${Variables.Colors.n100};
`;

export const ClaimSubtitle = styled.div`
    font-size: 12px;
    color: ${Variables.Colors.n50};
    margin-top: 2px;
`;

export const ClaimBalance = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.Colors.n100};
    font-variant-numeric: tabular-nums;
    flex: none;
`;

export const ApplySummary = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
    font-size: 12px;
    color: ${Variables.Colors.n70};
`;

export const ApplySummaryTotal = styled.span`
    font-weight: 600;
    color: ${Variables.Colors.n100};
    font-variant-numeric: tabular-nums;
`;

export const ButtonGroup = styled(ButtonBar)`
    margin-top: 24px;
    margin-bottom: 8px;
    text-align: right;
`;

/** Paper + content overrides for statement ledger dialogs (matches HTML 440px). */
export const LEDGER_DIALOG_PAPER_PROPS = {
    style: {
        width: 440,
        maxWidth: '92vw',
    },
} as const;

export const LEDGER_DIALOG_SX = {
    '& .MuiDialogContent-root': {
        minWidth: '0 !important',
        width: '100%',
    },
} as const;
